package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.JuridicoHistoricoSyncStatus
import br.com.vvrefeicoes.portal.domain.model.JuridicoProcesso
import br.com.vvrefeicoes.portal.domain.model.JuridicoProcessoFilterOptions
import br.com.vvrefeicoes.portal.domain.model.JuridicoProcessoPage
import br.com.vvrefeicoes.portal.domain.model.JuridicoProcessoUnidade
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.JuridicoProcessoRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteJuridicoProcessoRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : JuridicoProcessoRepository {
    override suspend fun list(
        numero: String?,
        tribunal: String?,
        status: String?,
        dataMinima: String?,
        dataMaxima: String?,
        page: Int,
        size: Int,
    ): JuridicoProcessoPage {
        val query =
            buildString {
                append("$baseUrl/juridico/processos?page=$page&size=$size")
                listOf("numero" to numero, "tribunal" to tribunal, "status" to status, "dataMinima" to dataMinima, "dataMaxima" to dataMaxima)
                    .forEach { (key, value) -> value?.takeIf(String::isNotBlank)?.let { append("&$key=${it.encodeURLParameter()}") } }
            }
        val response = httpClient.get(query) { authorize() }
        ensureSuccess(response)
        return response.body<JuridicoProcessoPageDto>().toDomain()
    }

    override suspend fun synchronizeHistorical(): Int {
        val response = httpClient.post("$baseUrl/juridico/processos/sincronizar-historico") { authorize() }
        ensureSuccess(response)
        return response.body<JuridicoHistoricoSyncResponseDto>().synchronizedProcesses
    }

    override suspend fun filterOptions(): JuridicoProcessoFilterOptions {
        val response = httpClient.get("$baseUrl/juridico/processos/filtros") { authorize() }
        ensureSuccess(response)
        return response.body<JuridicoProcessoFilterOptionsDto>().toDomain()
    }

    override suspend fun historicalSyncStatus(): JuridicoHistoricoSyncStatus {
        val response = httpClient.get("$baseUrl/juridico/processos/sincronizar-historico/status") { authorize() }
        ensureSuccess(response)
        return response.body<JuridicoHistoricoSyncStatusDto>().toDomain()
    }

    override suspend fun updateUnits(
        processoId: String,
        unidadeIds: List<String>,
    ): JuridicoProcesso {
        val response =
            httpClient.put("$baseUrl/juridico/processos/$processoId/unidades") {
                authorize()
                contentType(ContentType.Application.Json)
                setBody(JuridicoProcessoUnitsRequestDto(unidadeIds))
            }
        ensureSuccess(response)
        return response.body<JuridicoProcessoDto>().toDomain()
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value
            ?.token
            ?.let { header(HttpHeaders.Authorization, "Bearer $it") }
    }

    private suspend fun ensureSuccess(response: HttpResponse) {
        if (response.status.isSuccess()) return
        val message = runCatching { Json { ignoreUnknownKeys = true }.decodeFromString<JuridicoProcessoApiError>(response.bodyAsText()).message }.getOrNull()
        error(message ?: "HTTP ${response.status.value}")
    }
}

@Serializable
private data class JuridicoProcessoPageDto(
    val content: List<JuridicoProcessoDto> = emptyList(),
    val page: JuridicoProcessoPageMetadata = JuridicoProcessoPageMetadata(),
) {
    fun toDomain() = JuridicoProcessoPage(content.map(JuridicoProcessoDto::toDomain), page.totalPages)
}

@Serializable
private data class JuridicoProcessoPageMetadata(
    val totalPages: Int = 1,
)

@Serializable
private data class JuridicoProcessoFilterOptionsDto(
    val status: List<String> = emptyList(),
    val tribunais: List<String> = emptyList(),
) {
    fun toDomain() = JuridicoProcessoFilterOptions(status, tribunais)
}

@Serializable
private data class JuridicoProcessoDto(
    val id: String = "",
    val empresaId: String = "",
    val empresaNome: String = "",
    val empresaCnpj: String = "",
    val numeroCnj: String = "",
    val tribunal: String = "",
    val orgaoVara: String = "",
    val assunto: String = "",
    val classe: String = "",
    val status: String = "",
    val autor: String = "",
    val reu: String = "",
    val juiz: String = "",
    val esfera: String = "",
    val rito: String = "",
    val instancia: String = "",
    val estrategico: Boolean = false,
    val valorCausa: Double? = null,
    val ultimaMovimentacao: String? = null,
    val linkExterno: String? = null,
    val unidades: List<JuridicoProcessoUnidadeDto> = emptyList(),
) {
    fun toDomain() =
        JuridicoProcesso(
            id,
            empresaId,
            empresaNome,
            empresaCnpj,
            numeroCnj,
            tribunal,
            orgaoVara,
            assunto,
            classe,
            status,
            autor,
            reu,
            juiz,
            esfera,
            rito,
            instancia,
            estrategico,
            valorCausa,
            ultimaMovimentacao,
            linkExterno,
            unidades.map { JuridicoProcessoUnidade(it.id, it.nome) },
        )
}

@Serializable
private data class JuridicoProcessoApiError(
    val message: String? = null,
)

@Serializable
private data class JuridicoHistoricoSyncResponseDto(
    val synchronizedProcesses: Int = 0,
)

@Serializable
private data class JuridicoHistoricoSyncStatusDto(
    val executionsToday: Long = 0,
    val dailyLimit: Long = 3,
) {
    fun toDomain() = JuridicoHistoricoSyncStatus(executionsToday, dailyLimit)
}

@Serializable
private data class JuridicoProcessoUnidadeDto(
    val id: String = "",
    val nome: String = "",
)

@Serializable
private data class JuridicoProcessoUnitsRequestDto(
    val unidadeIds: List<String>,
)
